import { ApiError, ApiResponseData } from '@/api/types'
import { TOKEN_KEY } from '@/features/auth'
import { getStorageItem, removeStorageItem } from '@/shared/lib/storage/localStorage'
import { QueryClient } from '@tanstack/react-query'
import axios, { AxiosError, AxiosInstance } from 'axios'

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15 * 1000, // 15 sec
      refetchInterval: 15 * 1000,
      refetchIntervalInBackground: true,
      retry: 1,
    },
  },
})

const API_TRADER_URL = import.meta.env.VITE_API_TRADER_URL || 'https://api.syncra.me'

export const axiosInstance: AxiosInstance = axios.create({
  baseURL: API_TRADER_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
})

axiosInstance.interceptors.request.use(
  (config) => {
    const token = getStorageItem<string | null>(TOKEN_KEY, null)
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

const handleApiError = (error: AxiosError<ApiResponseData>): ApiError => {
  const apiError: ApiError = {
    status: error.response?.status || 500,
    message: error.response?.data?.message || error.message || 'Unknown error',
    errors: error.response?.data?.errors || undefined,
  }

  if (apiError.status === 401) {
    window.location.href = '/login'
    removeStorageItem(TOKEN_KEY)
  }

  return apiError
}

axiosInstance.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiResponseData>) => {
    const apiError = handleApiError(error)
    return Promise.reject(apiError)
  }
)
